import {
  Component,
  Input,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { Subscription } from 'rxjs';
import { buildConfigurableTooltipFormatter } from '../../utils/chart.utils';

interface DonutSegment {
  label: string;
  value: number;
  color: string;
  key?: string;
}

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-donut-chart',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe],
  templateUrl: './donut-chart.component.html',
  styleUrls: ['./donut-chart.component.scss'],
})
export class DonutChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  @ViewChild('legendContainer')
  legendContainerRef!: ElementRef<HTMLDivElement>;

  private myChart!: echarts.ECharts;
  private resizeObserver?: ResizeObserver;

  chartData: DonutSegment[] = [];
  statusData: Record<string, any> = {};

  showLeftArrow = false;
  showRightArrow = false;

  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private configService = inject(ConfigService);

  protected originalSeriesData: any[] = [];
  protected allSliceNames: string[] = [];

  // Wrapper handles: filter subscription, legend sync, broadcasting, toggle logic
  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();

  // Template-bound getter (donut-chart.component.html references currentlySelectedIndices)
  get currentlySelectedIndices(): number[] { return this.wrapper.currentlySelectedIndices; }
  getChartHeight(): string | null {
    const widget = this.data;
    const width = window.innerWidth;

    const normalize = (h: any): string | null => {
      if (h === undefined || h === null || h === '') return null;
      if (typeof h === 'number') return `${h}px`;
      const s = String(h).trim();
      if (!s) return null;
      return /^\d+(\.\d+)?$/.test(s) ? `${s}px` : s;
    };

    if (widget && typeof widget === 'object') {
      if (width < 768) {
        const sm = normalize(widget.heightSm);
        if (sm) return sm;
      }
      if (width < 1024) {
        const md = normalize(widget.heightMd);
        if (md) return md;
      }
      const lg = normalize(widget.heightLg);
      if (lg) return lg;

      const base = normalize(widget.height);
      if (base) return base;
    }

    return null;
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => this.allSliceNames,
      getCategories: () => [],
      // Custom highlight: donut uses opacity-based dim on pie slices (not ECharts legend)
      onSelectLegendsByIndices: (indices) => this.dimNonSelectedSlices(indices),
    }, 'legend-only');

    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.myChart?.resize());

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.chartData?.length || !this.myChart) return;
      const chartDom = document.getElementById(this.data?.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = undefined as any;
      this.buildChart();
    });
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.myChart?.dispose();
  }

  @HostListener('window:resize')
  onResize() {
    this.myChart?.resize();
    this.updateArrowVisibility();
  }

  protected loadAndRenderChart(filters?: any): void {
    this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
      next: (res) => {
        let chartData: DonutSegment[] = [];

        const raw = res?.responseList?.[0]?.['ui-data']?.[0]?.data;
        console.log("API RAW DATA:", raw);
        this.statusData = raw || {};

        if (raw && this.data?.mapping) {
          chartData = this.data.mapping.map((m: any) => ({
            label: m.label,
            value: raw[m.key] ?? 0,
            color: m.color,
            key: m.key
          }));
        }

        this.chartData = chartData;
        this.allSliceNames = chartData.map((seg) => seg.label);
        this.buildChart();

        setTimeout(() => this.updateArrowVisibility(), 0);
      },
      error: (err) => {
        console.error('Error loading donut chart data:', err);
      },
    });
  }

  protected transformDonutData(
    rawData: any[],
    categoryKey: string,
    valueKey: string,
  ): DonutSegment[] {
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const defaultPalette = theme.chart?.palette || [
      '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
      '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc',
    ];
    const colors = this.data?.colors || defaultPalette;
    const topNColors: string[] = this.data?.chartConfig?.topNColorsByStackPosition || [];
    const colorMap: { [k: string]: string } = this.data?.chartConfig?.colorMap || {};

    const getColor = (name: string, idx: number) => {
      if (topNColors && topNColors.length > idx && topNColors[idx]) return topNColors[idx];
      if (colorMap && colorMap[name]) return colorMap[name];
      return colors[idx % colors.length];
    };

    let transformedData = rawData.map((item, index) => ({
      label: item[categoryKey],
      value: item[valueKey] || 0,
      color: getColor(item[categoryKey], index),
    }));

    const stackOrder = (this.data?.stackOrder || 'desc').toLowerCase();
    if (stackOrder === 'asc') {
      transformedData.sort((a, b) => (a.value ?? 0) - (b.value ?? 0));
    } else {
      transformedData.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    }

    transformedData = transformedData.map((item, idx) => ({
      ...item,
      color: getColor(item.label, idx),
    }));

    return transformedData;
  }

  protected buildChart(): void {
    if (!this.chartData?.length) return;

    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
      this.attachResizeObserver(chartDom);
    }

    const topN = this.data?.top || this.chartData.length;

    const labelStyle = {
      color: theme.chart?.textColor || '#333',
      textBorderColor: 'transparent',
      textBorderWidth: 0,
      textShadowBlur: 0,
      textShadowColor: 'transparent',
    };

    const seriesData = this.chartData.map((seg, index) => {
      const dataItem: any = {
        value: seg.value,
        name: seg.label,
        key: seg.key,
        itemStyle: { color: seg.color },
      };

      if (index < topN) {
        dataItem.label = {
          show: true,
          position: 'inside',
          formatter: (params: any) => {
            const key: string = (params.data && params.data.key) || '';
            const name: string = params.name || '';
            const pct = (typeof params.percent === 'number') ? Math.round(params.percent) : (params.percent || '');
            const revenueSuffix = key.toLowerCase().includes('revenue') ? ` (${key})` : '';
            // Always show segment name and percentage; append key for revenue slices
            if (name) {
              return `${name}${revenueSuffix}\n${pct}%`;
            }
            return `${pct}%`;
          },
          fontSize: 12,
          fontWeight: 'bold',
          color: '#fff',
          align: 'center',
          verticalAlign: 'middle'
        };
      } else {
        dataItem.label = { show: false };
      }

      return dataItem;
    });

    const option: echarts.EChartsOption = {
      title: {
        text: 'Mine Level\nParameters\nWeightage',
        left: 'center',
        top: 'center',
        textStyle: {
          fontSize: 14,
          fontWeight: 'bold',
          lineHeight: 15,
          color: '#333',
          align: 'center'
        }
      },
      tooltip: {
        trigger: 'item',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          const configurableFormatter = buildConfigurableTooltipFormatter(tooltipCfg);
          if (configurableFormatter) return configurableFormatter;

          return (param: any) => {
            const categoryKey = this.data?.categoryKey || 'Category';
            const valueKey = this.data?.valueKey || 'Value';
            const value =
              typeof param.value === 'number' ? param.value.toLocaleString('en-US') : param.value;
            return `<div style="text-align:left;"><div><b>${categoryKey}:</b> ${param.name}</div><div><b>${valueKey}:</b> ${value}</div></div>`;
          };
        })(),
      },
      legend: {
        show: false,
      },
      series: [
  {
    name: this.data?.seriesName || this.data?.valueKey || '',
    type: 'pie',

    clockwise: true,
    startAngle: 90,

    radius: [this.data?.innerRadius || '32%', this.data?.outerRadius || '68%'], // slightly thicker donut
    center: ['50%', '50%'],

    data: seriesData,

    label: {                          // ✅ ADD THIS
      show: true,
      position: 'inside',
      formatter: '{d}%',
      fontSize: 12,
      fontWeight: 'bold',
      color: '#fff'
    },

    labelLine: {                      // ✅ ADD THIS
      show: false
    },

    minAngle: 6,                      // ✅ ensures small slices still show label

    avoidLabelOverlap: false,         // ✅ prevents pushing labels outside

    emphasis: {
      itemStyle: {
        shadowBlur: 15,
        shadowOffsetX: 0,
        shadowColor: 'rgba(0, 0, 0, 0.7)',
      },
      label: {
        show: false,
        position: 'center',
        formatter: '',
        fontSize: 14
      }
    }
  }
],
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option);
    this.originalSeriesData = JSON.parse(JSON.stringify(seriesData));

    // Wrapper re-applies visual state and wires click/legend events
    this.wrapper.afterRender();

    if (this.data?.filterKey) {
      // Keep custom click for chart area — wrapper handles the rest
    }

    setTimeout(() => this.myChart?.resize(), 0);
  }

  protected attachResizeObserver(chartDom: HTMLElement): void {
    if (this.resizeObserver || typeof ResizeObserver === 'undefined') return;

    this.resizeObserver = new ResizeObserver(() => {
      // Ensure ECharts recomputes pie radius/center when widget/card size changes.
      this.myChart?.resize();
      this.updateArrowVisibility();
    });

    this.resizeObserver.observe(chartDom);
    if (chartDom.parentElement) {
      this.resizeObserver.observe(chartDom.parentElement);
    }
  }

  // Legend item click handler (called from template)
  onLegendItemClick(item: DonutSegment): void {
    if (!this.data?.enableLegendClick) return;
    // Delegate to wrapper's slice click logic (handles toggle + broadcast)
    const seriesNames = this.allSliceNames;
    const clickedIndex = seriesNames.indexOf(item.label);
    if (clickedIndex === -1) return;

    // Toggle using wrapper state
    if (this.data?.isMultiSelect) {
      const pos = this.wrapper.currentlySelectedIndices.indexOf(clickedIndex);
      if (pos === -1) {
        this.wrapper.currentlySelectedIndices = [...this.wrapper.currentlySelectedIndices, clickedIndex];
      } else {
        this.wrapper.currentlySelectedIndices = this.wrapper.currentlySelectedIndices.filter(i => i !== clickedIndex);
      }
    } else {
      if (this.wrapper.currentlySelectedIndices.length === 1 && this.wrapper.currentlySelectedIndices[0] === clickedIndex) {
        this.wrapper.currentlySelectedIndices = [];
      } else {
        this.wrapper.currentlySelectedIndices = [clickedIndex];
      }
    }

    if (this.wrapper.currentlySelectedIndices.length === 0) {
      this.wrapper.selectLegendsByIndices([]);
      this.wrapper.broadcastLegendSync([]);
    } else {
      this.wrapper.selectLegendsByIndices(this.wrapper.currentlySelectedIndices);
      this.wrapper.broadcastLegendSync(this.wrapper.currentlySelectedIndices);
    }
  }

  // Custom highlight: dim non-selected slices (used by wrapper via onSelectLegendsByIndices)
  protected dimNonSelectedSlices(selectedIndices: number[]): void {
    if (!this.originalSeriesData.length) return;
    if (selectedIndices.length === 0) {
      this.resetHighlight();
      return;
    }

    const updated = this.originalSeriesData.map((item: any, idx: number) => ({
      ...item,
      itemStyle: { ...item.itemStyle, opacity: selectedIndices.includes(idx) ? 1 : 0.3 },
    }));
    this.myChart.setOption({ series: [{ data: updated }] });
  }

  protected resetHighlight(): void {
    if (!this.originalSeriesData.length) return;
    const reset = this.originalSeriesData.map((item: any) => ({
      ...item,
      itemStyle: { ...item.itemStyle, opacity: 1 },
    }));
    this.myChart.setOption({ series: [{ data: reset }] });
  }

  // LEGEND PAGINATION

  scrollLegend(direction: 'left' | 'right'): void {
    const element = this.legendContainerRef?.nativeElement;
    if (!element) return;

    const scrollAmount = element.clientWidth * 0.8;
    element.scrollBy({
      left: direction === 'right' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  }

  updateArrowVisibility(): void {
    if (!this.legendContainerRef) return;
    const element = this.legendContainerRef.nativeElement;
    this.showLeftArrow = element.scrollLeft > 0;
    this.showRightArrow = element.scrollWidth - element.scrollLeft - element.clientWidth > 1;
  }
}
